import {Injectable} from '@angular/core';

/**
 * Define la estructura del objeto de respuesta de la validación.
 * Incluye el documento formateado si la validación es exitosa.
 */
export interface DocumentValidationsResults {
  isValid: boolean;
  docType: 'CPF' | 'CNPJ' | 'Desconocido';
  errors: string[];
  doc?: string; // Propiedad opcional para el documento formateado
}

/**
 * Centraliza los mensajes de error estandarizados para la validación.
 */
export enum ValidationErrors {
  EMPTY_INPUT = 'The document cannot be empty.',
  INVALID_LENGTH = 'The document length is invalid.',
  ALL_DIGITS_SAME = 'Documents with all identical digits are invalid.',
  INVALID_CPF_CHECKSUM = 'The CPF check digit is incorrect, which means the document is invalid.',
  INVALID_CNPJ_CHECKSUM = 'The check digit of the CNPJ is incorrect, which means that the document is invalid.',
  _INVALID_CPF_CHECKSUM = 'The CPF check digit is incorrect.',
  _INVALID_CNPJ_CHECKSUM = 'The CNPJ check digit is incorrect.'
}

@Injectable({
  providedIn: 'root'
})
export class CpfCnpjValidatorsService {

  // --- Pesos para los Cálculos de Dígitos Verificadores ---

  /** Pesos para el cálculo del primer dígito verificador del CPF. */
  private readonly CPF_WEIGHTS_DV1 = [10, 9, 8, 7, 6, 5, 4, 3, 2];
  /** Pesos para el cálculo del segundo dígito verificador del CPF. */
  private readonly CPF_WEIGHTS_DV2 = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];

  /** Pesos para el cálculo del primer dígito verificador del CNPJ. */
  private readonly CNPJ_WEIGHTS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  /** Pesos para el cálculo del segundo dígito verificador del CNPJ. */
  private readonly CNPJ_WEIGHTS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  /**
   * Valida asíncronamente un documento (CPF o CNPJ) y devuelve un objeto detallado.
   * Si es válido, el objeto de respuesta incluye el documento formateado.
   * @param doc El número de documento a validar, puede contener formato.
   * @returns Una promesa que se resuelve con un objeto DocumentValidationsResults.
   */
  public async validate(doc: string | null | undefined): Promise<DocumentValidationsResults> {
    if (!doc) {
      return {isValid: false, docType: 'Desconocido', errors: [ValidationErrors.EMPTY_INPUT]};
    }

    const docDigits = this.sanitize(doc);
    const docLength = docDigits.length;

    if (docLength === 11) {
      const errors = this.getCpfErrors(docDigits);
      const isValid = errors.length === 0;
      return {
        isValid,
        docType: 'CPF',
        errors,
        doc: isValid ? this.formatCpf(docDigits) : undefined,
      };
    }

    if (docLength === 14) {
      const errors = this.getCnpjErrors(docDigits);
      const isValid = errors.length === 0;
      return {
        isValid,
        docType: 'CNPJ',
        errors,
        doc: isValid ? this.formatCnpj(docDigits) : undefined,
      };
    }

    return {
      isValid: false,
      docType: 'Desconocido',
      errors: [ValidationErrors.INVALID_LENGTH],
    };
  }

  /**
   * Obtiene una lista de errores de validación para un CPF.
   * @param cpfDigits El CPF saneado (solo dígitos).
   * @returns Un array de strings con los errores encontrados.
   */
  private getCpfErrors(cpfDigits: string): string[] {
    const errors: string[] = [];

    if (this.hasAllSameDigits(cpfDigits)) {
      errors.push(ValidationErrors.ALL_DIGITS_SAME);
    }

    const baseDigits = cpfDigits.substring(0, 9);
    const calculatedDV1 = this.calculateVerifierDigit(baseDigits, this.CPF_WEIGHTS_DV1);

    const digitsForDV2 = baseDigits + calculatedDV1;
    const calculatedDV2 = this.calculateVerifierDigit(digitsForDV2, this.CPF_WEIGHTS_DV2);

    const actualDV1 = parseInt(cpfDigits.charAt(9));
    const actualDV2 = parseInt(cpfDigits.charAt(10));

    if (calculatedDV1 !== actualDV1 || calculatedDV2 !== actualDV2) {
      errors.push(ValidationErrors.INVALID_CPF_CHECKSUM);
    }

    return errors;
  }

  /**
   * Obtiene una lista de errores de validación para un CNPJ.
   * @param cnpjDigits El CNPJ saneado (solo dígitos).
   * @returns Un array de strings con los errores encontrados.
   */
  private getCnpjErrors(cnpjDigits: string): string[] {
    const errors: string[] = [];

    if (this.hasAllSameDigits(cnpjDigits)) {
      errors.push(ValidationErrors.ALL_DIGITS_SAME);
    }

    const baseDigits = cnpjDigits.substring(0, 12);
    const calculatedDV1 = this.calculateVerifierDigit(baseDigits, this.CNPJ_WEIGHTS_DV1);

    const digitsForDV2 = baseDigits + calculatedDV1;
    const calculatedDV2 = this.calculateVerifierDigit(digitsForDV2, this.CNPJ_WEIGHTS_DV2);

    const actualDV1 = parseInt(cnpjDigits.charAt(12));
    const actualDV2 = parseInt(cnpjDigits.charAt(13));

    if (calculatedDV1 !== actualDV1 || calculatedDV2 !== actualDV2) {
      errors.push(ValidationErrors.INVALID_CNPJ_CHECKSUM);
    }

    return errors;
  }

  /**
   * Elimina caracteres no numéricos de una cadena.
   * @param value La cadena a limpiar.
   * @returns Una cadena que contiene solo dígitos.
   */
  private sanitize(value: string): string {
    return value.replace(/\D/g, '');
  }

  /**
   * Formatea una cadena de dígitos de CPF.
   * @param cpfDigits La cadena de 11 dígitos a formatear.
   * @returns El CPF formateado (XXX.XXX.XXX-XX).
   */
  private formatCpf(cpfDigits: string): string {
    return cpfDigits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  /**
   * Formatea una cadena de dígitos de CNPJ.
   * @param cnpjDigits La cadena de 14 dígitos a formatear.
   * @returns El CNPJ formateado (XX.XXX.XXX/XXXX-XX).
   */
  private formatCnpj(cnpjDigits: string): string {
    return cnpjDigits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }

  /**
   * Comprueba si todos los caracteres de una cadena son iguales.
   * @param value La cadena de dígitos a comprobar.
   * @returns `true` si todos los caracteres son iguales, `false` en caso contrario.
   */
  private hasAllSameDigits(value: string): boolean {
    return /^(\d)\1+$/.test(value);
  }

  /**
   * Calcula un dígito verificador basado en el algoritmo de Módulo 11.
   * @param digits La base de dígitos para el cálculo.
   * @param weights El array de pesos para la multiplicación.
   * @returns El dígito verificador calculado (0-9).
   */
  private calculateVerifierDigit(digits: string, weights: number[]): number {
    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
      sum += parseInt(digits.charAt(i)) * weights[i];
    }
    const remainder = sum % 11;
    return remainder < 2 ? 0 : 11 - remainder;
  }
}
